import { useState } from 'react';
import { toast } from 'sonner';

import {
  previewRecipientImport,
  recipientImportNeedsReview,
  type FeedbackRecipient,
  type RecipientImportPreview,
} from '@/core/lib/parse-feedback-recipients';

export function useFeedbackRecipientImport(input: {
  recipients: FeedbackRecipient[];
  onRecipientsChange: (recipients: FeedbackRecipient[]) => void;
  remainingSlots: number | null;
  onImported?: (added: number) => void;
  noneReadyMessage?: string;
}) {
  const [preview, setPreview] = useState<RecipientImportPreview | null>(null);

  const applyReady = (ready: FeedbackRecipient[]) => {
    if (ready.length === 0) return;
    input.onRecipientsChange([...input.recipients, ...ready]);
    input.onImported?.(ready.length);
  };

  const requestImport = (
    incoming: FeedbackRecipient[],
    options?: { skipCleanReview?: boolean },
  ) => {
    if (incoming.length === 0) return;
    const nextPreview = previewRecipientImport({
      existing: input.recipients,
      incoming,
      remainingSlots: input.remainingSlots,
    });
    if (!recipientImportNeedsReview(nextPreview, incoming.length, options)) {
      applyReady(nextPreview.ready);
      return;
    }
    if (
      nextPreview.ready.length === 0 &&
      nextPreview.invalid.length +
        nextPreview.duplicate.length +
        nextPreview.overQuota.length <=
        1
    ) {
      if (input.noneReadyMessage) {
        toast.error(input.noneReadyMessage);
      }
      return;
    }
    setPreview(nextPreview);
  };

  const confirmPreview = () => {
    if (!preview) return;
    applyReady(preview.ready);
    setPreview(null);
  };

  return {
    preview,
    requestImport,
    confirmPreview,
    cancelPreview: () => setPreview(null),
  };
}
